import { useState } from "react";
import {
  ServiceBlock,
  BLOCK_TYPES,
  getBlockLabel,
  getBlockDescription,
  getAvailableBlockTypes,
} from "@/lib/serviceBlocks";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  ChevronUp,
  ChevronDown,
  Plus,
  Trash2,
  GripVertical,
  LayoutList,
} from "lucide-react";

interface ServiceBlockEditorProps {
  blocks: ServiceBlock[];
  onChange: (blocks: ServiceBlock[]) => void;
}

const ServiceBlockEditor = ({ blocks, onChange }: ServiceBlockEditorProps) => {
  const [addingType, setAddingType] = useState<string>("");
  const availableTypes = getAvailableBlockTypes(blocks);

  const moveBlock = (index: number, direction: -1 | 1) => {
    const newIndex = index + direction;
    if (newIndex < 0 || newIndex >= blocks.length) return;
    const updated = [...blocks];
    [updated[index], updated[newIndex]] = [updated[newIndex], updated[index]];
    onChange(updated);
  };

  const toggleBlock = (index: number) => {
    const updated = [...blocks];
    updated[index] = { ...updated[index], enabled: !updated[index].enabled };
    onChange(updated);
  };

  const removeBlock = (index: number) => {
    onChange(blocks.filter((_, i) => i !== index));
  };

  const addBlock = () => {
    if (!addingType) return;
    onChange([...blocks, { type: addingType, enabled: true }]);
    setAddingType("");
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2 mb-4">
        <LayoutList className="h-4 w-4 text-muted-foreground" />
        <h3 className="text-sm font-medium">Page Sections</h3>
        <span className="text-xs text-muted-foreground">
          ({blocks.filter((b) => b.enabled).length} active)
        </span>
      </div>

      {/* Block list */}
      <div className="space-y-1">
        {blocks.map((block, index) => (
          <div
            key={`${block.type}-${index}`}
            className={`flex items-center gap-2 rounded-md border px-3 py-2 transition-colors ${
              block.enabled
                ? "bg-card border-border"
                : "bg-muted/30 border-border/50 opacity-60"
            }`}
          >
            <GripVertical className="h-3.5 w-3.5 text-muted-foreground/50 shrink-0" />

            {/* Up/Down arrows */}
            <div className="flex flex-col gap-0.5 shrink-0">
              <button
                onClick={() => moveBlock(index, -1)}
                disabled={index === 0}
                className="p-0.5 rounded hover:bg-accent disabled:opacity-30 disabled:cursor-not-allowed"
                title="Move up"
              >
                <ChevronUp className="h-3 w-3" />
              </button>
              <button
                onClick={() => moveBlock(index, 1)}
                disabled={index === blocks.length - 1}
                className="p-0.5 rounded hover:bg-accent disabled:opacity-30 disabled:cursor-not-allowed"
                title="Move down"
              >
                <ChevronDown className="h-3 w-3" />
              </button>
            </div>

            {/* Label + description */}
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium truncate">
                {getBlockLabel(block.type)}
              </p>
              <p className="text-[11px] text-muted-foreground truncate">
                {getBlockDescription(block.type)}
              </p>
            </div>

            {/* Enable/disable toggle */}
            <Switch
              checked={block.enabled}
              onCheckedChange={() => toggleBlock(index)}
              className="shrink-0"
            />

            {/* Remove button */}
            <button
              onClick={() => removeBlock(index)}
              className="p-1 rounded hover:bg-destructive/10 text-muted-foreground hover:text-destructive transition-colors shrink-0"
              title="Remove section"
            >
              <Trash2 className="h-3.5 w-3.5" />
            </button>
          </div>
        ))}
      </div>

      {/* Add section */}
      {availableTypes.length > 0 && (
        <div className="flex items-center gap-2 pt-2 border-t border-border">
          <Select value={addingType} onValueChange={setAddingType}>
            <SelectTrigger className="text-xs h-8 flex-1">
              <SelectValue placeholder="Add a section..." />
            </SelectTrigger>
            <SelectContent>
              {availableTypes.map((type) => (
                <SelectItem key={type} value={type}>
                  {getBlockLabel(type)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button
            size="sm"
            variant="outline"
            onClick={addBlock}
            disabled={!addingType}
            className="h-8"
          >
            <Plus className="h-3.5 w-3.5 mr-1" />
            Add
          </Button>
        </div>
      )}

      {blocks.length === 0 && (
        <p className="text-xs text-muted-foreground text-center py-4">
          No sections configured. Add sections above or they will use the default layout.
        </p>
      )}
    </div>
  );
};

export default ServiceBlockEditor;
